/**
 * EmpresasSection — Tab "Empresas" en Ajustes (rc19+).
 *
 * Permite al admin gestionar sub-empresas multi-tenant. Cada empresa lleva
 * sus propias credenciales Toku (account_id + account_key) por ambiente
 * (sandbox + production). Al asignar un cliente a una empresa, sus facturas
 * se sincronizan con la cuenta Toku correcta.
 *
 * El api_key y webhook_secret siguen siendo globales (org-level) en
 * Ajustes/Pagos/Configuración — los datos por-empresa son solo las account_*.
 *
 * Solo visible para usuarios con permiso `empresas.write`.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Plus, Pencil, CheckCircle2, AlertCircle, AlertTriangle, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiCall, type ApiError } from "@/lib/api";

/* ─── Tipos ─────────────────────────────────────────────────── */
type Empresa = {
  id: number;
  nombre: string;
  razon_social: string | null;
  rfc: string | null;
  direccion_fiscal: string | null;
  notas: string | null;
  activa: boolean;
  has_sandbox_creds: boolean;
  has_production_creds: boolean;
  toku_sandbox_account_id: string | null;
  toku_production_account_id: string | null;
  created_at: string;
  updated_at: string;
};

type EmpresaPayload = {
  nombre?: string;
  razon_social?: string | null;
  rfc?: string | null;
  direccion_fiscal?: string | null;
  notas?: string | null;
  toku_sandbox_account_id?: string | null;
  toku_sandbox_account_key?: string | null;
  toku_production_account_id?: string | null;
  toku_production_account_key?: string | null;
  activa?: boolean;
};

/* ─── Hooks ─────────────────────────────────────────────────── */
function useEmpresas() {
  return useQuery<Empresa[]>({
    queryKey: ["empresas"],
    queryFn: () => apiCall<Empresa[]>("get", "/api/v1/empresas"),
    staleTime: 10_000,
  });
}

/* ─── Componente principal ──────────────────────────────────── */
export function EmpresasSection() {
  const { data: empresas = [], isLoading } = useEmpresas();
  const [editId, setEditId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  // rc124 — ambiente activo de Toku (para GRITAR si una empresa activa no
  // tiene Account Key de ese ambiente: sus cobros NO se sincronizan).
  const { data: tokuCfg } = useQuery<{ enabled: boolean; environment: "sandbox" | "production" }>({
    queryKey: ["toku-config", "for-empresas-warning"],
    queryFn: () => apiCall("get", "/api/v1/integrations/payments/toku/config"),
    staleTime: 60_000,
    retry: 0,
  });
  const envActivo = tokuCfg?.environment;
  const sinKey = (tokuCfg?.enabled && envActivo)
    ? empresas.filter((e) =>
        e.activa && !(envActivo === "production" ? e.has_production_creds : e.has_sandbox_creds))
    : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">
            Sub-empresas que operan dentro de este sistema. Cada cliente pertenece a una
            empresa, y sus cobros Toku se enrutan a la cuenta de cobro correspondiente.
            El <strong>API Key</strong> Toku y el <strong>Webhook Secret</strong> son
            globales (un solo endpoint webhook recibe eventos de todas las cuentas) —
            se configuran en <em>Pagos / Configuración</em>.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus size={14} className="mr-1" />
          Agregar empresa
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 size={14} className="animate-spin" />
          Cargando empresas…
        </div>
      )}

      {sinKey.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/60 bg-destructive/10 p-3 text-xs text-destructive">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            <p className="font-semibold">
              {sinKey.map((e) => e.nombre).join(", ")}: sin Account Key de {envActivo} —
              los cobros de {sinKey.length === 1 ? "esta empresa" : "estas empresas"} NO se
              sincronizan con Toku.
            </p>
            <p className="mt-0.5">
              Sus clientes no generan customer/CLABE ni facturas en Toku hasta que pegues
              la Account Key ({envActivo}) de su cuenta de cobro en <strong>Editar</strong>.
              La encuentras en el panel de Toku › Ajustes › Cuentas de cobro.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {empresas.map((emp) => (
          <EmpresaRow key={emp.id} emp={emp} onEdit={() => setEditId(emp.id)} />
        ))}
        {empresas.length === 0 && !isLoading && (
          <div className="rounded border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
            No hay empresas configuradas todavía.
          </div>
        )}
      </div>

      {creating && (
        <EmpresaFormDialog
          empresa={null}
          onClose={() => setCreating(false)}
        />
      )}
      {editId !== null && (
        <EmpresaFormDialog
          empresa={empresas.find((e) => e.id === editId) ?? null}
          onClose={() => setEditId(null)}
        />
      )}
    </div>
  );
}

/* ─── Row ─────────────────────────────────────────────────── */
function EmpresaRow({ emp, onEdit }: { emp: Empresa; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-card p-3">
      <div className="flex items-center gap-3">
        <div className={`rounded-full p-2 ${emp.activa ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
          <Building2 size={14} />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{emp.nombre}</span>
            <span className="text-[10px] text-muted-foreground">#{emp.id}</span>
            {!emp.activa && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                Inactiva
              </span>
            )}
          </div>
          {emp.razon_social && (
            <span className="text-[11px] text-muted-foreground">
              {emp.razon_social}
              {emp.rfc ? ` · ${emp.rfc}` : ""}
            </span>
          )}
          <div className="mt-1 flex items-center gap-2 text-[10px]">
            <CredsBadge label="Sandbox" ok={emp.has_sandbox_creds} accountId={emp.toku_sandbox_account_id} />
            <CredsBadge label="Producción" ok={emp.has_production_creds} accountId={emp.toku_production_account_id} />
          </div>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onEdit}>
        <Pencil size={12} className="mr-1" />
        Editar
      </Button>
    </div>
  );
}

function CredsBadge({ label, ok, accountId }: { label: string; ok: boolean; accountId: string | null }) {
  return (
    <div className={`flex items-center gap-1 rounded px-1.5 py-0.5 ${ok ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
      {ok ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
      <span>
        {label}
        {ok && accountId ? `: ${accountId.slice(0, 12)}…` : ""}
      </span>
    </div>
  );
}

/* ─── Form dialog (crear + editar) ───────────────────────────── */
function EmpresaFormDialog({
  empresa,
  onClose,
}: {
  empresa: Empresa | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = empresa !== null;

  const [nombre, setNombre] = useState(empresa?.nombre ?? "");
  const [razonSocial, setRazonSocial] = useState(empresa?.razon_social ?? "");
  const [rfc, setRfc] = useState(empresa?.rfc ?? "");
  const [direccionFiscal, setDireccionFiscal] = useState(empresa?.direccion_fiscal ?? "");
  const [notas, setNotas] = useState(empresa?.notas ?? "");
  const [activa, setActiva] = useState(empresa?.activa ?? true);
  const [sandboxAccountId, setSandboxAccountId] = useState(empresa?.toku_sandbox_account_id ?? "");
  const [sandboxAccountKey, setSandboxAccountKey] = useState(""); // siempre vacío (secreto)
  const [prodAccountId, setProdAccountId] = useState(empresa?.toku_production_account_id ?? "");
  const [prodAccountKey, setProdAccountKey] = useState(""); // siempre vacío

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: EmpresaPayload = {
        nombre,
        razon_social: razonSocial || null,
        rfc: rfc || null,
        direccion_fiscal: direccionFiscal || null,
        notas: notas || null,
        toku_sandbox_account_id: sandboxAccountId || null,
        toku_production_account_id: prodAccountId || null,
        activa,
      };
      // Las account_keys solo se mandan si el admin las teclea (no rotamos vacío→null automáticamente).
      // Si el admin quiere borrar un secret, mete "DELETE" en el campo y backend lo maneja.
      // Por simplicidad: si el campo está vacío, NO lo mandamos (el backend lo deja como está).
      if (sandboxAccountKey) payload.toku_sandbox_account_key = sandboxAccountKey;
      if (prodAccountKey) payload.toku_production_account_key = prodAccountKey;

      if (isEdit) {
        return apiCall<Empresa>("patch", `/api/v1/empresas/${empresa!.id}`, { json: payload });
      }
      return apiCall<Empresa>("post", "/api/v1/empresas", { json: payload });
    },
    onSuccess: () => {
      toast.success(isEdit ? "Empresa actualizada" : "Empresa creada");
      qc.invalidateQueries({ queryKey: ["empresas"] });
      onClose();
    },
    onError: (err: ApiError) => {
      toast.error(err.message || "Error al guardar empresa");
    },
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Editar empresa #${empresa!.id}` : "Crear empresa nueva"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          {/* ─── Identificación ─── */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Nombre *</Label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre corto (ej. la marca)"
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Razón social</Label>
              <Input
                value={razonSocial}
                onChange={(e) => setRazonSocial(e.target.value)}
                placeholder="Razón social S.A. de C.V."
                className="text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">RFC</Label>
              <Input
                value={rfc}
                onChange={(e) => setRfc(e.target.value.toUpperCase())}
                placeholder="BYT123456ABC"
                maxLength={13}
                className="text-sm font-mono"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-xs">Dirección fiscal</Label>
            <Textarea
              value={direccionFiscal}
              onChange={(e) => setDireccionFiscal(e.target.value)}
              rows={2}
              className="text-sm"
              placeholder="Opcional"
            />
          </div>

          {/* ─── Creds Toku Sandbox ─── */}
          <div className="rounded border border-border p-2">
            <p className="mb-1 text-xs font-semibold">Toku — Sandbox</p>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] uppercase text-muted-foreground">Account ID</Label>
                <Input
                  value={sandboxAccountId}
                  onChange={(e) => setSandboxAccountId(e.target.value)}
                  placeholder="acc_xxxxx"
                  className="text-xs font-mono"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] uppercase text-muted-foreground">
                  Account Key {empresa?.has_sandbox_creds && "(••• configurado, deja vacío para no cambiar)"}
                </Label>
                <Input
                  type="password"
                  value={sandboxAccountKey}
                  onChange={(e) => setSandboxAccountKey(e.target.value)}
                  placeholder={empresa?.has_sandbox_creds ? "••••••••••••" : "Pegar account key"}
                  className="text-xs font-mono"
                />
              </div>
            </div>
          </div>

          {/* ─── Creds Toku Producción ─── */}
          <div className="rounded border border-border p-2">
            <p className="mb-1 text-xs font-semibold">Toku — Producción</p>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] uppercase text-muted-foreground">Account ID</Label>
                <Input
                  value={prodAccountId}
                  onChange={(e) => setProdAccountId(e.target.value)}
                  placeholder="acc_xxxxx"
                  className="text-xs font-mono"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] uppercase text-muted-foreground">
                  Account Key {empresa?.has_production_creds && "(••• configurado, deja vacío para no cambiar)"}
                </Label>
                <Input
                  type="password"
                  value={prodAccountKey}
                  onChange={(e) => setProdAccountKey(e.target.value)}
                  placeholder={empresa?.has_production_creds ? "••••••••••••" : "Pegar account key"}
                  className="text-xs font-mono"
                />
              </div>
            </div>
          </div>

          {/* ─── Notas + activa ─── */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Notas internas</Label>
            <Textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={2}
              className="text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={activa}
              onChange={(e) => setActiva(e.target.checked)}
            />
            Empresa activa (clientes pueden sincronizar con Toku)
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saveMutation.isPending}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !nombre.trim()}
          >
            {saveMutation.isPending ? (
              <Loader2 size={12} className="mr-1 animate-spin" />
            ) : (
              <Save size={12} className="mr-1" />
            )}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
